import React from 'react';
import { MdDashboard, MdPushPin, MdArrowBack } from 'react-icons/md';
import ChartWrapper from '../components/ChartWrapper';
import accountsData from '../data/accounts.json';

const bankingData = accountsData.bankingData;

function detectGoalFromText(text) {
  const amountPatterns = [
    /goal[^$\n]{0,30}\$([0-9,]+)/i,
    /save[^$\n]{0,20}\$([0-9,]+)/i,
    /target[^$\n]{0,20}\$([0-9,]+)/i,
    /\$([0-9,]+)[^.\n]{0,30}(?:goal|target|savings?)/i,
    /(?:fund|payment|vacation|car|home|house)[^$\n]{0,20}\$([0-9,]+)/i,
  ];
  let targetAmount = null;
  for (const p of amountPatterns) {
    const m = text.match(p);
    if (m) {
      const val = parseFloat(m[1].replace(/,/g, ''));
      if (val >= 500) { targetAmount = val; break; }
    }
  }
  if (!targetAmount) return null;

  const namePatterns = [
    /(?:saving[s]?\s+for|goal[:\s]+(?:to\s+)?(?:save\s+)?(?:for\s+)?)\s*(?:an?\s+)?([a-z][a-z\s]{2,28}?)(?:\s+of|\s+goal|\s+fund|,|\.|$)/i,
    /([a-z][a-z\s]{2,20}(?:fund|payment|vacation|car|home|trip|emergency))/i,
    /(emergency fund|down payment|vacation|retirement|new car|home purchase|wedding|education)/i,
  ];
  let goalName = 'Savings Goal';
  for (const p of namePatterns) {
    const m = text.match(p);
    if (m && m[1] && m[1].trim().length > 3) {
      goalName = m[1].trim().replace(/\b\w/g, c => c.toUpperCase());
      break;
    }
  }
  return { goalName, targetAmount };
}

const DashboardScreen = ({ onNavigate, onBack, savedItems, onUnpin }) => {
  const currentSavings = bankingData.reduce((sum, acc) => {
    const val = parseFloat((acc.balance || '').replace(/[$,]/g, ''));
    return sum + (isNaN(val) || val < 0 ? 0 : val);
  }, 0);

  const formatCurrency = (n) => '$' + Math.round(n).toLocaleString('en-CA');

  return (
    <div className="screen dashboard-screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate && onNavigate('insightsScreen'))} aria-label="Back">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">My Dashboard</h1>
        </div>
        <MdDashboard size={24} style={{ color: 'var(--bh-black)' }} />
      </header>

      <div className="scroll-content gray-bg">
        {savedItems.length === 0 ? (
          <div className="dashboard-empty">
            <MdPushPin className="dashboard-empty-icon" />
            <h3>Nothing saved yet</h3>
            <p>Tap <strong>Save to Dashboard</strong> on any chat response to pin it here. Goals you discuss will automatically show live progress.</p>
          </div>
        ) : (
          [...savedItems].reverse().map(item => {
            const goal = detectGoalFromText(item.text);
            const progress = goal
              ? Math.min((currentSavings / goal.targetAmount) * 100, 100)
              : null;
            const onTrack = progress !== null && progress >= 40;

            return (
              <div key={item.id} className="dashboard-card">
                <div className="dashboard-card-header">
                  <div className="dashboard-card-meta">
                    <MdPushPin style={{ fontSize: 12, marginRight: 5, color: 'var(--td-green)' }} />
                    <span className="dashboard-card-timestamp">{item.timestamp}</span>
                  </div>
                  <button className="dashboard-card-unpin" onClick={() => onUnpin(item.id)} title="Remove">✕</button>
                </div>

                <div className="dashboard-card-body">
                  <ChartWrapper text={item.text} />
                </div>

                {goal && (
                  <div className="goal-widget">
                    <div className="goal-widget-header">
                      <div>
                        <div className="goal-widget-name">{goal.goalName}</div>
                        <div className="goal-widget-target-label">Target: {formatCurrency(goal.targetAmount)}</div>
                      </div>
                      <span className="goal-widget-badge" style={{ background: onTrack ? 'var(--td-green)' : '#f59e0b' }}>
                        {onTrack ? 'On Track' : 'In Progress'}
                      </span>
                    </div>

                    <div className="goal-widget-current">{formatCurrency(currentSavings)}</div>
                    <div className="goal-widget-amounts">
                      <span>Current total savings</span>
                      <span>{progress.toFixed(0)}% of goal</span>
                    </div>
                    <div className="progress-bar-bg" style={{ marginTop: 8 }}>
                      <div
                        className="progress-fill"
                        style={{
                          width: `${progress}%`,
                          backgroundColor: onTrack ? 'var(--td-green)' : '#f59e0b',
                        }}
                      />
                    </div>
                    <div className="goal-widget-footer">
                      <span>Still needed: {formatCurrency(Math.max(0, goal.targetAmount - currentSavings))}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DashboardScreen;
